import React from 'react';
import { Helmet } from 'react-helmet-async';
import { canonicalUrl as toCanonicalUrl } from '../utils/seoPolicy';
import {
  getProductTitle,
  getProductDescription,
  getProductKeywords
} from '../utils/dataFallbacks';

export function SEOMetadata({
  title,
  description,
  keywords = [],
  canonicalUrl,
  type = 'website',
  imageUrl,
  prevPage,
  nextPage,
  products = [],
  breadcrumbs = [],
  lastModified
}) {
  const baseUrl = 'https://www.techieday.com';
  // Robots and canonical are NOT emitted here: SEORobotsTag (routes) / DynamicPage (guides) are the only authorities.
  const pageUrl = toCanonicalUrl(canonicalUrl);

  /* =========================
     Website Schema
  ========================= */
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "TechieDay",
    "url": baseUrl,
    "description":
      "Your ultimate destination for tech deals, price comparisons, and product reviews",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${baseUrl}/search/{search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  /* =========================
     Breadcrumb Schema
     (ONLY real URLs)
  ========================= */
  const breadcrumbSchema =
    breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbs.map((crumb, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": crumb.name,
            "item": `${baseUrl}${crumb.url}` // MUST be a real page
          }))
        }
      : null;

  /* =========================
     ItemList Schema (LIST PAGES ONLY)
     🚫 No offers
     🚫 No aggregateRating
  ========================= */
  const productListSchema =
    products.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "numberOfItems": products.length,
          "itemListElement": products.slice(0, 12).map((product, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@id": `${baseUrl}/dp/${product.asin}`, // ✅ FIXED
              "name": getProductTitle(product)
            }
          }))
        }
      : null;

  return (
    <Helmet>
      {/* =========================
          BASIC META
      ========================= */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />

      {/* =========================
          FRESHNESS SIGNALS
      ========================= */}
      {lastModified && (
        <>
          <meta
            property="article:modified_time"
            content={new Date(lastModified).toISOString()}
          />
          <meta
            name="last-modified"
            content={new Date(lastModified).toISOString()}
          />
        </>
      )}

      {/* =========================
          CANONICAL + PAGINATION
      ========================= */}
      {prevPage && <link rel="prev" href={`${baseUrl}${prevPage}`} />}
      {nextPage && <link rel="next" href={`${baseUrl}${nextPage}`} />}

      {/* =========================
          OPEN GRAPH
      ========================= */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      {pageUrl && <meta property="og:url" content={pageUrl} />}
      <meta property="og:site_name" content="TechieDay" />
      <meta property="og:locale" content="en_US" />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      {imageUrl && <meta property="og:image:alt" content={title} />}

      {/* =========================
          TWITTER
      ========================= */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}

      {/* =========================
          STRUCTURED DATA
      ========================= */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {productListSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productListSchema)}
        </script>
      )}
    </Helmet>
  );
}
